import type { ProjectProductionStatus } from '@mogul/api-client';
import React from 'react';

import crown from '../../common/svg/movie-voting/crown_icon.svg';
import { NotificationCard } from '../movie-voting/NotificationCard.component';
import type {
  AdditionalInfoProps,
  DetailsProps,
} from './card/AdditionalInfo.component';
import { AdditionalInfo } from './card/AdditionalInfo.component';
import styles from './FinancedProject.module.css';

export interface FinancedProjectProps
  extends DetailsProps,
    AdditionalInfoProps {
  name: string;
  studio: string;
  image: string;
}

interface FullFinancedProjectProps extends FinancedProjectProps {
  trailerLink: string;
  productionStatus: ProjectProductionStatus;
}

export const FinancedProject: React.FC<FullFinancedProjectProps> = props => {
  const {
    trailerLink,
    productionStatus,
    budget,
    name,
    studio,
    image,
    description,
    cast,
    additionalDetails,
  } = props;
  const movieInfoProps = { budget, additionalDetails };

  return (
    <div className="flex flex-col items-center w-4/5">
      <div className="max-w-screen-lg">
        <NotificationCard
          image={crown}
          imageClassName="p-1 mr-2 h-8 rounded-full bg-yellow-300"
          title="Project Financed"
          body={`${name} has been financed through Mogul.`}
        />
        <div className="fontVanguard font-bold xl:text-7xl md:text-6xl text-4xl mt-8">
          {name}
        </div>
        <div className="text-secondary mt-3">{studio}</div>
        <div className="flex w-full flex-row mt-8 mb-10 flex-wrap xl:flex-nowrap justify-center">
          <img
            className={styles.poster + ' xl:mr-8 rounded'}
            alt="movie poster"
            src={image}
          />

          <div className="flex flex-col xl:mt-0 mt-8">
            <div className="flex flex-col w-full">
              <div className="">
                <span
                  className={
                    styles.statusContainer +
                    ' px-4 py-2 font-noto-sans font-bold uppercase opacity-70 border rounded-lg'
                  }
                >
                  Current status
                </span>
              </div>

              <div className="text-primary font-vanguard font-bold text-5xl mt-3">
                {productionStatus.replace('_', '-')}
              </div>
              <div className="text-white font-noto-sans text-xs mt-4 opacity-70">
                {description}
              </div>
            </div>

            <div className="flex flex-col w-full">
              <div className="text-primary font-vanguard font-bold text-5xl mt-6">
                Team
              </div>
              <div className="grid grid-flow-row grid-cols-4 grid-rows-3 gap-4 my-4 opacity-70">
                {cast.map((c, i) => (
                  <div key={i}>
                    <div
                      className={' font-noto-sans text-xs font-bold uppercase'}
                    >
                      {c.role}
                    </div>
                    <a
                      target="_blank"
                      className="hover:underline cursor-pointer"
                      href={c.imdbUrl}
                      rel="noreferrer"
                    >
                      {c.name}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div
          className={
            styles.iframeContainer + ' relative overflow-hidden w-full'
          }
        >
          <iframe
            className="absolute inset-0 w-full h-full"
            title="Trailer Link"
            src={trailerLink}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>

        <AdditionalInfo {...movieInfoProps} />
      </div>
    </div>
  );
};
